import styled, { css } from 'styled-components/macro';
import * as colors from 'src/styles/colors';
import { useCallback, useEffect, useState } from 'react';
import { useModalStore, useStore } from 'src/hooks/useStore';
import useService from 'src/hooks/useService';
import { Controller, useForm } from 'react-hook-form';
import { informServiceProvider } from 'src/api/service/informService';
import { errorHandler } from 'src/api/errorHandler';

import Modal from 'src/components/ui-kit/Modal';
import ReactSelect from 'src/components/ui-kit/ReactSelect';
import Input from 'src/components/ui-kit/Input';
import Captcha from 'src/components/ui-kit/Captcha';
import InformImg from 'src/assets/images/tipo_inform.png';
import { IReqInform, InformOptions } from 'src/types/informTypes';
import { mobilePoint } from 'src/styles/globalStyle';
import { getCaptchaErrMsg } from 'src/utils/indexs';

const Wrapper = styled.div`
  width: 100%;
  padding: 32px 40px;
  text-align: center;

  background-image: url(${InformImg});
  background-repeat: no-repeat;
  background-size: auto 84px;
  background-position: bottom 0px right 0px;
`;

const FormItem = styled.div`
  display: grid;
  justify-content: space-between;
  margin-bottom: 12px;
  grid-template-columns: 1fr 4fr;
  gap: 8px;

  @media (max-width: ${mobilePoint}) {
    grid-template-columns: 1fr;
  }
`;
const Title = styled.div<{ required?: boolean }>`
  font-size: 16px;
  line-height: 40px;
  color: ${colors.BlueGray_800};
  position: relative;
  text-align: left;

  ${(props) =>
    props.required &&
    css`
      padding-left: 8px;
      :before {
        content: '•';
        color: ${colors.notice_red};
        position: absolute;
        font-size: 20px;
        top: -12px;
        left: -6px;
      }
    `}
  @media(max-width:${mobilePoint}) {
    display: flex;
    gap: 0.5em;
  }
`;
const ErrorMsg = styled.div`
  color: ${colors.notice_error};
  line-height: 1em;
  @media (max-width: ${mobilePoint}) {
    line-height: inherit;
  }
`;
const Content = styled.div`
  display: flex;
  font-size: 16px;
  line-height: 40px;
  color: ${colors.BlueGray_800};
  gap: 5px;

  @media (max-width: ${mobilePoint}) {
    display: grid;
    text-align: left;
  }
`;
const Textarea = styled.textarea`
  border-radius: 4px;
  border: 1px solid ${colors.primaryBlue_100};
  width: 100%;
  padding: 8px 11px;

  ::placeholder {
    color: ${colors.BlueGray_200};
  }
  :focus {
    outline: none;
    border: 1px solid ${colors.primaryBlue_500};
  }
  :focus {
    outline: 2px solid #000000;
    outline-offset: -2px;
  }
`;
const Button = styled.button`
  width: max-content;
  height: 40px;
  border-radius: 4px;
  background-color: ${colors.primaryGreen_700};
  color: ${colors.white};
  font-size: 18px;
  margin-top: 2em;
  padding: 0 1em;
  cursor: pointer;
`;

const informService = informServiceProvider();

/**
 * 資料錯誤通報視窗
 */
const InformModal: React.FC = () => {
  const closeModal = useModalStore((state) => state.closeModal);
  const { control, getValues, setValue, reset } = useForm<IReqInform>();
  const isMobile = useStore((state) => state.isMobile);
  const path = useService();
  const [captchaUriSuffix, setCaptchaUriSuffix] = useState(Date.now());
  const [captchaUuid, setCaptchaUuid] = useState('');
  // 欄位檢查
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isNameOk, setIsNameOk] = useState<boolean>(false);
  const [isEmailOk, setIsEmailOk] = useState<boolean>(false);
  const [isDescriptionOk, setIsDescriptionOk] = useState<boolean>(false);
  const [isCaptchaOk, setIsCaptchaOk] = useState<boolean>(false);
  // 是否送出成功
  const [isReported, setIsReported] = useState<boolean>(false);
  // 錯誤訊息
  const [emailErrMsg, setEmailErrMsg] = useState<string>('請輸入信箱');
  const [captchaErrMsg, setCaptchaErrMsg] = useState<string>('請輸入驗證碼');

  //插入通報項目預設值
  useEffect(() => {
    // 依當前路徑設定通報項目
    const getDataSetId = () => {
      switch (path) {
        case '/patentRights':
          return InformOptions[0].value;
        case '/trademarkRights':
          return InformOptions[1].value;
        default:
          return InformOptions[0].value;
      }
    };
    reset({
      dataSetId: getDataSetId(),
    });
  }, [reset, path]);

  // 插入驗證碼UUID
  useEffect(() => {
    setValue('captchaUuid', captchaUuid);
  }, [setValue, captchaUuid]);

  /**檢驗信箱格式並回傳錯誤訊息。 */
  const getEmailErrMsg = (email?: string) => {
    let errMsg = null;
    if (email) {
      const emailRegxp = /[\w-]+@([\w-]+\.)+[\w-]+/;
      //validate ok or not
      if (!emailRegxp.test(email)) {
        errMsg = '請輸入正確信箱格式';
      }
    } else {
      errMsg = '請輸入信箱';
    }
    return errMsg;
  };

  /**欄位檢查 */
  const handleCheck = () => {
    setIsChecked(true);
    getValues('name') ? setIsNameOk(true) : setIsNameOk(false);
    getValues('description') ? setIsDescriptionOk(true) : setIsDescriptionOk(false);
    getValues('captchaCode') ? setIsCaptchaOk(true) : setIsCaptchaOk(false);

    const errMsg = getEmailErrMsg(getValues('email'));
    if (!!errMsg) {
      setEmailErrMsg(errMsg);
    } else {
      setIsEmailOk(true);
    }
  };

  const handleSubmit = useCallback(async () => {
    // 欄位無空，則送出請求
    if (isNameOk && isEmailOk && isDescriptionOk && isCaptchaOk) {
      try {
        await informService.sendInformMail(getValues());
        setIsReported(true);
      } catch (e) {
        const errorMsg = errorHandler(e);
        setIsCaptchaOk(false);
        errorMsg && setCaptchaErrMsg(getCaptchaErrMsg(errorMsg));
      }
    }
  }, [isNameOk, isEmailOk, isDescriptionOk, isCaptchaOk, getValues]);

  useEffect(() => {
    handleSubmit();
  }, [handleSubmit]);
  
  return (
    <Modal title={'資料錯誤通報'}>
      {isReported ? (
        <Wrapper>
          <Content>感謝您的通報，我們將於收信後盡快為您回覆與處理。</Content>
          <Button onClick={() => closeModal()} >關閉</Button>
        </Wrapper>
      ) : (
        <Wrapper>
          <FormItem>
            <Title>
              <div><label htmlFor="input-dataSetId">開放資料名稱</label></div>
            </Title>
            <Content>
              <Controller
                control={control}
                name={`dataSetId`}
                render={({ field: { onChange, value } }) => (
                  <ReactSelect
                    options={InformOptions}
                    onChange={onChange}
                    value={value}
                    width="100%"
					inputId="input-dataSetId"
                  />
                )}
              />
            </Content>
          </FormItem>
          <FormItem>
            <Title required>
              <div><label htmlFor="input-name">姓名(必填)</label></div>
              {isChecked && !isNameOk && <ErrorMsg>請輸入姓名。</ErrorMsg>}
            </Title>
            <Content>
              <Controller
                control={control}
                name={`name`}
                render={({ field: { onChange } }) => (
                  <Input placeholder={'請輸入您的姓名'} onChange={onChange} width="100%" maxLength={10} inputId="input-name" />
                )}
              />
            </Content>
          </FormItem>
          <FormItem>
            <Title required>
              <div><label htmlFor="input-email">電子郵件信箱(必填)</label></div>
              {isChecked && !isEmailOk && <ErrorMsg>{emailErrMsg}</ErrorMsg>}
            </Title>
            <Content>
              <Controller
                control={control}
                name={`email`}
                render={({ field: { onChange } }) => (
                  <Input placeholder={'請輸入您的電子郵件信箱'} onChange={onChange} width="100%" maxLength={100} inputId="input-email" />
                )}
              />
            </Content>
          </FormItem>
          <FormItem>
            <Title required>
              <div><label htmlFor="input-description">問題描述(必填)</label></div>
              {isChecked && !isDescriptionOk && <ErrorMsg>請輸入問題。</ErrorMsg>}
            </Title>
            <Content>
              <Controller
                control={control}
                name={`description`}
                render={({ field: { onChange } }) => (
                  <Textarea
                    placeholder={'請輸入問題描述，限100字以內'}
                    rows={5}
                    onChange={onChange}
                    maxLength={100}
					id="input-description"
                  />
                )}
              />
            </Content>
          </FormItem>
          <FormItem>
            <Title required>
              <div><label htmlFor="input-captchaCode">驗證碼(必填)</label></div>
              {isChecked && !isCaptchaOk && <ErrorMsg>{captchaErrMsg}</ErrorMsg>}
            </Title>
            <Content>
              <Controller
                control={control}
                name={`captchaCode`}
                render={({ field: { onChange } }) => (
                  <Input
                    placeholder={'輸入驗證碼'}
                    onChange={onChange}
                    width={isMobile ? '100%' : '40%'}
					inputId="input-captchaCode"
                  />
                )}
              />
              <Captcha
                captchaUuid={captchaUuid}
                setCaptchaUuid={setCaptchaUuid}
                captchaUriSuffix={captchaUriSuffix}
                setCaptchaUriSuffix={setCaptchaUriSuffix}
              />
            </Content>
          </FormItem>
          <Button
            onClick={() => {
              handleCheck();
            }}
          >
            我要通報
          </Button>
        </Wrapper>
      )}
    </Modal>
  );
};

export default InformModal;
